import { Link as RouterLink } from 'react-router-dom';
import { Description, Heading } from '@heroui/react';
import { AppBrand } from '../../../components/ui/app-brand';
import { ThemeToggle } from '../../../components/ui/theme-toggle';
import { getSupportEmail } from '../../../lib/support';
import { LandingFooter } from '../components/landing-footer';

const SECTIONS: Array<{ id: string; title: string; paragraphs: string[] }> = [
  {
    id: 'introducao',
    title: '1. Introdução',
    paragraphs: [
      'Esta Política de Privacidade descreve como o Link Park (“nós”, “nosso”) trata dados pessoais no aplicativo e no site, em conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018).',
      'Os dados de identificação do controlador (razão social, CNPJ e endereço) serão atualizados nesta página assim que estiverem disponíveis. Enquanto isso, o canal de contato para privacidade e exercício de direitos é o e-mail de suporte indicado abaixo.',
    ],
  },
  {
    id: 'dados',
    title: '2. Quais dados coletamos',
    paragraphs: [
      'Conta e autenticação: identificadores e credenciais gerenciados pelo Firebase Authentication (por exemplo e-mail, UID e, quando aplicável, dados de provedores como Google), além de status de verificação de e-mail e telefone quando o fluxo do app exigir.',
      'Perfil: nome de exibição, foto de perfil (quando enviada), telefone e demais informações que você preenche para completar o cadastro.',
      'Condomínio e vagas: associação a condomínios, blocos e apartamentos; vagas de garagem; janelas de disponibilidade; solicitações e histórico de uso de vagas; e, quando houver, relatos de uso indevido, inclusive fotos anexadas por você.',
      'Notificações: preferências de notificação e tokens de push necessários para enviar alertas no dispositivo, quando você autorizar.',
      'Assistente (quando ativo): mensagens e contexto das conversas com o assistente do app, para prestar o serviço e melhorar a experiência.',
      'Dados técnicos: registros de uso, diagnóstico e segurança (por exemplo endereço IP, tipo de dispositivo e user-agent), e ferramentas de análise de sessão quando habilitadas no ambiente.',
    ],
  },
  {
    id: 'finalidades',
    title: '3. Para que usamos os dados',
    paragraphs: [
      'Prestação do serviço de compartilhamento e gestão de vagas no condomínio; autenticação e manutenção da conta; comunicação operacional (pedidos, respostas e alertas); suporte ao usuário; segurança, prevenção a fraudes e cumprimento de obrigações legais.',
      'Bases legais típicas (LGPD): execução de contrato ou de procedimentos preliminares (art. 7º, V); legítimo interesse para segurança, melhoria e métricas agregadas, quando cabível (art. 7º, IX); consentimento quando exigido (por exemplo notificações push ou cookies/analytics não essenciais); e cumprimento de obrigação legal ou regulatória (art. 7º, II).',
    ],
  },
  {
    id: 'compartilhamento',
    title: '4. Compartilhamento e subprocessadores',
    paragraphs: [
      'Não vendemos seus dados pessoais. Podemos compartilhar dados com: outros moradores/gestores do mesmo condomínio na medida necessária ao funcionamento do app (ex.: pedidos de vaga); prestadores que processam dados em nosso nome; e autoridades, quando houver obrigação legal.',
      'Subprocessadores típicos: Google Firebase (Authentication, Cloud Messaging e, quando usado, Storage) e o provedor de hospedagem da API e do frontend. Esses fornecedores tratam dados conforme seus próprios termos e medidas de segurança.',
    ],
  },
  {
    id: 'retencao',
    title: '5. Retenção e segurança',
    paragraphs: [
      'Mantemos os dados pelo tempo necessário para cumprir as finalidades desta política, inclusive prazos legais, resolução de disputas e segurança da plataforma. Após isso, excluímos ou anonimizamos quando viável.',
      'Adotamos medidas técnicas e organizacionais razoáveis para proteger os dados. Nenhum sistema é totalmente isento de riscos; se você suspeitar de acesso indevido à conta, altere a senha e contate o suporte.',
    ],
  },
  {
    id: 'direitos',
    title: '6. Seus direitos (titular)',
    paragraphs: [
      'Você pode solicitar confirmação de tratamento, acesso, correção, anonimização, bloqueio ou eliminação de dados desnecessários, portabilidade (quando aplicável), informação sobre compartilhamentos, revogação de consentimento e oposição a tratamentos em bases legais cabíveis, nos termos da LGPD.',
      'Para exercer esses direitos ou tirar dúvidas sobre privacidade, envie um e-mail para o canal de suporte. Podemos pedir confirmação de identidade antes de atender a solicitação.',
    ],
  },
  {
    id: 'criancas',
    title: '7. Crianças e adolescentes',
    paragraphs: [
      'O Link Park é destinado a adultos no contexto de condomínios e uso de vagas. Não coletamos intencionalmente dados de crianças. Se soubermos de cadastro indevido, tomaremos medidas para remover os dados.',
    ],
  },
  {
    id: 'alteracoes',
    title: '8. Alterações',
    paragraphs: [
      'Podemos atualizar esta política periodicamente. A versão vigente estará sempre nesta página, com a data de atualização indicada no topo. Mudanças relevantes podem ser comunicadas pelo app ou por e-mail, quando apropriado.',
    ],
  },
];

export default function PrivacyPolicyPage() {
  const supportEmail = getSupportEmail();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-separator bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between gap-3 px-4 sm:px-6">
          <RouterLink to="/site" className="min-w-0">
            <AppBrand iconSize={18} markClassName="size-8 rounded-md" />
          </RouterLink>
          <div className="flex items-center gap-2">
            <RouterLink to="/login" className="text-sm font-medium text-accent">
              Entrar
            </RouterLink>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
        <p className="text-muted text-sm">Atualizado em 22 de setembro de 2026</p>
        <Heading level={1} className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
          Política de Privacidade
        </Heading>
        <Description className="mt-3 text-base leading-relaxed">
          Como o Link Park trata dados pessoais no app e no site, de forma alinhada à LGPD.
        </Description>

        <div className="mt-10 space-y-10">
          {SECTIONS.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-24 space-y-3">
              <Heading level={2} className="text-xl font-semibold">
                {section.title}
              </Heading>
              {section.paragraphs.map((text) => (
                <p key={text.slice(0, 48)} className="text-muted text-sm leading-relaxed sm:text-base">
                  {text}
                </p>
              ))}
            </section>
          ))}

          <section id="contato" className="scroll-mt-24 space-y-3 border-t border-separator pt-10">
            <Heading level={2} className="text-xl font-semibold">
              9. Contato
            </Heading>
            <p className="text-muted text-sm leading-relaxed sm:text-base">
              Dúvidas sobre esta política ou pedidos relacionados a dados pessoais:{' '}
              <a className="text-accent font-medium underline-offset-2 hover:underline" href={`mailto:${supportEmail}`}>
                {supportEmail}
              </a>
              .
            </p>
          </section>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
